import dayjs from "dayjs";
import { useState, useContext } from "react";
import { getPartnerLogo } from "../../service/logo.service";
import {
  Button,
  Card,
  Link,
  useTheme,
  Stack,
  Box,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  applyCpfMask,
  parseCompanyName,
  parsePaymentMethod,
} from "../../service/utils.service";
import { convertToBrazilianDate } from "../../service/date.service";
import QRCode from "react-qr-code";
import { DoubtCard } from "../doubt-card/doubt-card.component";
import { UserContext } from "../../context/user.context";
import { cancelOrder } from "../../service/order.service";

const PassengerBasicInfo = ({ passenger }) => {
  const { fullName, cpf, birthDate } = passenger;
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      sx={{ width: "80%" }}
    >
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Nome:
        </Typography>
        <Typography variant="body2">{fullName}</Typography>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          CPF:
        </Typography>
        <Typography variant="body2">{applyCpfMask(cpf)}</Typography>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Data de Nascimento:
        </Typography>
        <Typography variant="body2">
          {convertToBrazilianDate(birthDate)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const OrderInformation = ({ order }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [statusOverride, setStatusOverride] = useState(null);
  const theme = useTheme();
  const { authUser } = useContext(UserContext);

  // Defensive programming: ensure order is a valid object
  if (!order || typeof order !== "object") {
    console.error("Invalid order object:", order);
    return null;
  }

  const {
    id,
    createdAt,
    status,
    insurance,
    passengers,
    tripInfo,
    paymentInfo,
    vouchers,
  } = order;

  // Ensure required properties exist and are valid
  if (!insurance || !passengers || !tripInfo || !paymentInfo) {
    console.error("Order missing required properties:", {
      insurance,
      passengers,
      tripInfo,
      paymentInfo,
    });
    return null;
  }

  let { boardingDate, arrivalDate } = tripInfo;
  const { insuranceCompany, insuranceName } = insurance;
  const { destination } = tripInfo;
  const { method, creditCardInfo, total, qrCodeBase64, qrCodeString } =
    paymentInfo;
  const {
    banner,
    fullName: cardFullName,
    number: cardNumber,
    installments,
  } = creditCardInfo;

  const tripDuration = arrivalDate
    ? dayjs(arrivalDate).diff(dayjs(boardingDate), "days") + 1
    : 0;

  // Safe status extraction and normalization - defensive programming
  const getSafeStatus = (status) => {
    if (typeof status === "string") {
      // Normalize common status values
      const normalized = status.toLowerCase();
      if (normalized === "approved" || normalized === "pago") {
        return "APPROVED";
      }
      return status;
    } else if (typeof status === "object" && status !== null) {
      // If status is an object, try to extract statusPagamento field
      if (
        status.statusPagamento &&
        typeof status.statusPagamento === "string"
      ) {
        const normalized = status.statusPagamento.toLowerCase();
        if (normalized === "approved" || normalized === "pago") {
          return "APPROVED";
        }
        return status.statusPagamento;
      }
      // Fallback: convert object to string representation for debugging
      console.warn("Status is an object, converting to string:", status);
      return "[OBJECT]";
    }
    // Fallback for null, undefined, or other types
    return "PENDING";
  };

  // statusOverride permite refletir o cancelamento sem recarregar a página
  const paymentStatus = statusOverride || getSafeStatus(status);

  // Rótulo em português para exibição (o valor interno segue em inglês p/ a lógica)
  const STATUS_LABELS = {
    APPROVED: "Aprovado",
    PENDING: "Pendente",
    REFUSED: "Recusado",
    PROCESSING: "Processando",
    CANCELLED: "Cancelado",
  };
  const statusLabel = STATUS_LABELS[paymentStatus] || paymentStatus;
  const statusColor =
    paymentStatus === "CANCELLED"
      ? theme.palette.error.main
      : theme.palette.primary.main;

  // Mostra só os 4 últimos dígitos do cartão (nunca o número completo)
  const cardLast4 = cardNumber
    ? String(cardNumber).replace(/\D/g, "").slice(-4)
    : "";

  const isCreditCard = method === "credit-card";

  // Cancelamento permitido: aprovado e ainda antes da data de embarque.
  const canCancel =
    paymentStatus === "APPROVED" &&
    !!boardingDate &&
    dayjs(boardingDate).startOf("day").isAfter(dayjs().startOf("day"));

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      const token = await authUser.getIdToken();
      await cancelOrder(id, token);
      setStatusOverride("CANCELLED");
      setCancelSuccess(true); // mantém o diálogo aberto mostrando o sucesso
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        "Não foi possível cancelar o pedido. Tente novamente ou fale com o suporte.";
      alert(message);
      setCancelDialogOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelSuccess(false);
  };
  const expandedClass = isExpanded ? "expanded" : "";

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const hanldleOpenVoucherClick = () => {
    vouchers.forEach((voucher) => {
      window.open(voucher.url, "_blank");
    });
  };

  const convertToBrl = (value) => {
    return value.toFixed(2).toLocaleString("pt-BR").replace(".", ",");
  };

  return (
    <Card
      elevation={12}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: "0.5rem", sm: "1rem", md: "2rem" },
        margin: 0,
        backgroundColor: `${theme.palette.grey[50]}`,
        boxShadow: "-8px 8px 30px -15px rgba(0,0,0,0.3)",
        paddingBottom: isExpanded ? "0" : "2rem",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          padding: { xs: "0.75rem 1rem", sm: "1rem 2rem" },
          backgroundColor: `${theme.palette.text.primary}`,
          color: "white",
        }}
      >
        <Typography variant="body1">
          Pedido <span style={{ textDecoration: "underline" }}>{id}</span>
        </Typography>
        <Typography variant="body1">
          {convertToBrazilianDate(createdAt)}
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        sx={{
          padding: { xs: "0 1rem", sm: "0 2rem" },
        }}
        spacing={2}
        mt={"1rem"}
      >
        <Box
          sx={{
            width: { xs: "210px", sm: "210px" },
            display: "flex",
            alignItems: "center",
            "& svg": { height: "100%", width: "100%" },
          }}
        >
          {getPartnerLogo(insuranceCompany).component.dark}
        </Box>
        <Stack>
          <Typography
            variant="body1"
            sx={{
              color: statusColor,
              fontWeight: "bolder",
            }}
          >
            {statusLabel}
          </Typography>
          <Typography variant="body2">
            {" "}
            <spam style={{ fontWeight: "bold" }}>Plano</spam> {insuranceName}
          </Typography>
        </Stack>
        <Stack>
          <Typography
            variant="body1"
            sx={{
              color: `${theme.palette.primary.main}`,
              fontWeight: "bolder",
            }}
          >
            Data e permanência
          </Typography>
          <Typography variant="body2">
            {convertToBrazilianDate(boardingDate)} até{" "}
            {convertToBrazilianDate(arrivalDate)}
          </Typography>
          <Typography variant="body2">
            Permanência de{" "}
            <spam style={{ fontWeight: "bold" }}>
              {tripDuration} {tripDuration === 1 ? "dia" : "dias"}
            </spam>
          </Typography>
        </Stack>

        <Stack
          sx={{
            flexShrink: { xs: 0, md: 1 },
            flexGrow: { xs: 1, md: 0 },
            justifyContent: { xs: "space-between", md: "center" },
            alignItems: { xs: "center", md: "center" },
            gap: "0.5rem",
            flexDirection: { xs: "row", md: "column" },
          }}
        >
          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={hanldleOpenVoucherClick}
            disabled={paymentStatus !== "APPROVED"}
          >
            Abrir voucher
          </Button>
          <Link
            onClick={handleExpandClick}
            sx={{
              color: `${theme.palette.grey[700]}`,
              fontSize: "0.875rem",
              cursor: "pointer",
            }}
          >
            {isExpanded ? "Menos" : "Mais"} detalhes
          </Link>
        </Stack>
      </Stack>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && closeCancelDialog()}
        maxWidth="xs"
        fullWidth
      >
        {cancelSuccess ? (
          <>
            <DialogTitle sx={{ color: "success.main" }}>
              Pedido cancelado!
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                O pedido {id} foi cancelado junto à seguradora. O reembolso
                será feito por ela no mesmo cartão da compra, dentro do prazo
                dela. O status do pedido já consta como{" "}
                <strong>Cancelado</strong>.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: "1rem 1.5rem" }}>
              <Button
                variant="contained"
                onClick={closeCancelDialog}
                sx={{ textTransform: "none" }}
              >
                Entendi
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Cancelar pedido {id}?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                O seguro será cancelado junto à seguradora e o reembolso será
                feito por ela no mesmo cartão da compra. Essa ação não pode ser
                desfeita. Deseja continuar?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: "1rem 1.5rem" }}>
              <Button
                onClick={closeCancelDialog}
                disabled={cancelling}
                sx={{ textTransform: "none" }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelOrder}
                disabled={cancelling}
                startIcon={
                  cancelling ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                sx={{ textTransform: "none" }}
              >
                {cancelling ? "Cancelando..." : "Confirmar cancelamento"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {isExpanded && (
        <>
          <Divider
            sx={{
              borderColor: `${theme.palette.primary.main}`,
              margin: "1rem 2rem-",
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={5}
            sx={{
              padding: { xs: "0 1rem", md: "0 2rem" },
              width: "100%",
            }}
          >
            <Stack
              spacing={1}
              sx={{
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: `${theme.palette.primary.main}`,
                  fontWeight: "bolder",
                }}
              >
                Informações do plano e seguro
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  width: "100%",
                }}
              >
                <Stack spacing={1} flex={1}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Seguradora
                  </Typography>
                  <Typography variant="body2">
                    {parseCompanyName(insuranceCompany)}
                  </Typography>
                </Stack>
                <Stack spacing={1} flex={1}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Plano
                  </Typography>
                  <Typography variant="body2">{insuranceName}</Typography>
                </Stack>
                <Stack spacing={1} flex={2}>
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Destino
                    </Typography>
                    <Typography variant="body2">{destination}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Divider
            sx={{
              borderColor: `${theme.palette.primary.main}`,
              margin: "1rem 2rem-",
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={5}
            sx={{
              padding: { xs: "0 1rem", md: "0 2rem" },
              width: "100%",
            }}
          >
            <Stack
              spacing={1}
              sx={{
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: `${theme.palette.primary.main}`,
                  fontWeight: "bolder",
                }}
              >
                Segurados
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                flexDirection={"flex-start"}
              >
                <Stack flex={1}>
                  {passengers.map((passenger, idx) => (
                    <PassengerBasicInfo key={idx} passenger={passenger} />
                  ))}
                </Stack>
                <DoubtCard />
              </Stack>
            </Stack>
          </Stack>
          {canCancel && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              sx={{ padding: { xs: "0 1rem", md: "0 2rem" } }}
            >
              <Link
                onClick={() => setCancelDialogOpen(true)}
                sx={{
                  color: `${theme.palette.error.main}`,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Solicitar cancelamento do pedido
              </Link>
            </Stack>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "center", md: "space-between" }}
            sx={{
              padding: "1rem 2rem",
              backgroundColor: `${theme.palette.primary.main}`,
              color: "white",
              textAlign: { xs: "center", md: "start" },
            }}
          >
            {/* {qrCodeBase64 && (
                <QRCode className="qr-code" value={qrCodeBase64} />
              )} */}
            <Typography variant="body1">
              Forma de pagamento:{" "}
              <Typography
                component="span"
                variant="inherit"
                sx={{ fontWeight: 700 }}
              >
                {parsePaymentMethod(method)}
                {isCreditCard && cardLast4 && ` - Final •••• ${cardLast4}`}
              </Typography>
            </Typography>

            <Typography variant="body1">
              Valor total:{" "}
              <span style={{ fontWeight: "bold" }}>
                R$ {convertToBrl(total)}
              </span>
            </Typography>
          </Stack>
        </>
      )}
    </Card>
  );
};

export default OrderInformation;
